import { LogicalOperations, LogicalOperator } from "../LogicalOperations";
import { ArrayUtils } from "../Utils/ArrayUtils";
import { ROLELESS_ROLE_ID } from "./constants";
import { IItemRights } from "./RightTypes";

//TODO: dokumentace
export class StandardOperationRights {
    private _activeRoleIds: readonly string[] = [];
    private _strictlyDisabled: boolean = false;

    private _canNew: boolean;
    private _canEdit: boolean = false;
    private _canDelete: boolean = false;
    private _isEditDirty: boolean = false;
    private _isDeleteDirty: boolean = false;

    private _editItemRights: IItemRights[];
    private _deleteItemRights: IItemRights[];

    private _contextEditRoleIds: string[] | null;
    private _contextDeleteRoleIds: string[] | null;

    public get canNew(): boolean {
        return this._canNew;
    }

    public set canNew(value: boolean) {
        this._canNew = value;
    }
    public get canEdit(): boolean | null {
        if (this._canEdit != null && !this._isEditDirty) {
            return this._canEdit;
        }

        if (this.editItemRights?.length > 0) {
            this._canEdit = this._combineRoleResults(this.editItemRights, this.contextEditRoleIds, true);
            this._setRightsRoles(this.editItemRights, this.contextEditRoleIds);
        }

        this._isEditDirty = false;

        return this._canEdit;
    }

    public set canEdit(value: boolean) {
        this._canEdit = value;
    }

    public get canDelete(): boolean | null {
        if (this._canDelete != null && !this._isDeleteDirty) {
            return this._canDelete;
        }

        if (this.deleteItemRights && this.deleteItemRights.length > 0) {
            this._canDelete = this._combineRoleResults(this.deleteItemRights, this.contextDeleteRoleIds, true);
            this._setRightsRoles(this.deleteItemRights, this.contextDeleteRoleIds);
        }

        this._isDeleteDirty = false;

        return this._canDelete;
    }

    public set canDelete(value: boolean) {
        this._canDelete = value;
    }

    /**
     * Z předaného seznamu rolí vyřadí ty, které nemají povolené odpovídající formulářové právo
     * @param itemRights Form práva
     * @param contextRoleIds Role, které mají právo pro daný kontext
     */
    private _setRightsRoles(itemRights: IItemRights[], contextRoleIds: string[]): void {
        const relevantRoleIds = contextRoleIds ? ArrayUtils.intersect([...this._activeRoleIds], contextRoleIds).filter(roleId => roleId !== ROLELESS_ROLE_ID) : [];

        if (relevantRoleIds.length > 0) {
            itemRights.filter(itemRight => !itemRight.value).forEach(itemRight => {
                const i = contextRoleIds.indexOf(itemRight.roleID);
                if (i >= 0)
                    contextRoleIds.splice(i, 1);
            });
        }
    }

    /**
     * Vrací výsledek kombinace práv tříd na rozhraní a form práv
     * @param roleLessItemRight Form právo bez role
     * @param itemRights Form práva
     * @param contextRoleIds Role, které mají právo pro daný kontext
     * @param noItemRightDefaultValue Defaultní hodnota form práva pro role, které nemají form právo definované
     * @returns Výsledné právo
     */
    private _combineRoleResults(itemRights: IItemRights[], contextRoleIds: string[], noItemRightDefaultValue: boolean): boolean | null {
        const roleLessItemRight = itemRights.find(itemRight => itemRight.roleID === ROLELESS_ROLE_ID); //TODO-rites: #takhle implicitní role kontrolovat nejde; c2: takhle to bude ok, implicitní !== bez role

        //zbytek validItemsRights (bez toho pro uživatele bez role) může pokračovat ve  vyhodnocení.
        //TODO-rites předtím tu tohle filtrování nebylo, ale to bylo asi špatně. V případě že by bez role bylo právo true a ostatní práva false, bylo by chybně povoleno.
        itemRights = itemRights.filter(itemRight => itemRight !== roleLessItemRight);

        // pokud je zakázáno právem bez role, tak už žádnou rolí povolit nejde
        if (roleLessItemRight?.value === false)
            return false;

        const relevantRoleIds = contextRoleIds ? ArrayUtils.intersect([...this._activeRoleIds], contextRoleIds).filter(roleId => roleId !== ROLELESS_ROLE_ID) : [];

        if (relevantRoleIds.length > 0) {
            const relevantItemRights = itemRights.filter(itemRight => relevantRoleIds.includes(itemRight.roleID));
            const relevantItemRightsValues = relevantItemRights.map(itemRight => itemRight.isNegative ? !itemRight.value : itemRight.value); //TODO: Zeptat se DT, jestli není potřeba uvažovat isNegative?
            const relevantItemRightsRoleIds = relevantItemRights.map(i => i.roleID);
            /* Pokud je více relevantních rolí z contextu, než kolik se našlo práv z formulářové úrovně pro tyto role
               Nebo existuje nějaká relavntní role, pro kterou nejsou definované itemRights,
               pak je jasné že nějaká role nemá nastavení na formulářové úrovni a je potřeba do vyhodnocení přidat defaultní hodnotu
             */
            if (relevantRoleIds.length > relevantItemRightsValues.length ||
                relevantRoleIds.some(roleId => !relevantItemRightsRoleIds?.includes(roleId)))
                relevantItemRightsValues.push(noItemRightDefaultValue);

            return LogicalOperations.binaryOperationOverRange(LogicalOperator.or, relevantItemRightsValues);
        }

        // Pokud nejsou žádné relevantní role, vracím null
        return null;
    }

    /**
     * Pokud je nastaveno na true, znamená že práva by měla být neměnná.
     */
    public get strictlyDisabled(): boolean {
        return this._strictlyDisabled;
    }
    public set strictlyDisabled(value: boolean) {
        this._strictlyDisabled = value;
    }

    public get editItemRights(): IItemRights[] {
        return this._editItemRights;
    }
    public set editItemRights(itemRights: IItemRights[]) {
        this._editItemRights = itemRights;
    }

    public get deleteItemRights(): IItemRights[] {
        return this._deleteItemRights;
    }
    public set deleteItemRights(itemRights: IItemRights[]) {
        this._deleteItemRights = itemRights;
    }

    public get contextEditRoleIds(): string[] | null {
        return this._contextEditRoleIds;
    }
    public set contextEditRoleIds(editRoleIds: string[] | null) {
        this._contextEditRoleIds = editRoleIds;
    }

    public get contextDeleteRoleIds(): string[] | null {
        return this._contextDeleteRoleIds;
    }
    public set contextDeleteRoleIds(deleteRoleIds: string[] | null) {
        this._contextDeleteRoleIds = deleteRoleIds;
    }

    /**
     * Vytvoří novou instanci.
     * @param activeRoleIds Aktivní role uživatele
     * @param canEdit Právo editovat
     * @param canDelete Právo mazat
     * @param canNew Právo zakládat nové záznamy
     * @param strictlyDisabled Pokud je nastaveno na true, znamená že práva by měla být neměnná.
     */
    constructor(activeRoleIds: readonly string[], canEdit: boolean = false, canDelete: boolean = false, canNew: boolean = false, strictlyDisabled: boolean = false) {
        this._activeRoleIds = activeRoleIds;
        this._canEdit = canEdit;
        this._canDelete = canDelete;
        this._canNew = canNew;
        this._strictlyDisabled = strictlyDisabled;
    }

    /*TODO: nešlo by nějaké jinak vyřešit isDirty? a setovat jen _edititemsRights?
    Takto tady máme 2 settery na stejný field bez nějakého vysvětlení kdy použít jaký.
    https://tfs.tescosw.loc/Jadro/Jadro%20.NET/_git/MultiWebClient/pullrequest/5212?_a=overview
    */
    public setEditRightsResults(editItemRights: IItemRights[], isDirty: boolean = true): void {
        this._editItemRights = editItemRights;
        this._isEditDirty = isDirty;
    }

    public setDeleteRightsResults(deleteItemRights: IItemRights[], isDirty: boolean = true): void {
        this._deleteItemRights = deleteItemRights;
        this._isDeleteDirty = isDirty;
    }
}
